import React, { useState, useEffect, useCallback, useRef } from "react";
import ProductTable from "./ProductTable";
import * as XLSX from "xlsx";
import Sidebar from "./Sidebar";
import Nav from "./Nav";
import AddProductModal from "./AddProductModal";
import ProductButton from "./ProductButton"; // Asegúrate de importar ProductButton
import Spinner from "./Spinner";
import { db } from "../firebaseConfig";
import {
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  collection,
  doc,
  query,
  where,
} from "firebase/firestore";
import Swal from "sweetalert2";
import moment from "moment";
import { useNavContext } from "../context/NavContext";
import { useLocation } from "react-router-dom"

moment.locale("es");

function parseCost(value) {
  // Quita símbolos y comas para poder parsear
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    return parseFloat(value.replace(/[^0-9.-]+/g, "")) || 0;
  }
  return 0;
}

const ArticulosCompra = () => {
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showNuevoButton, setShowNuevoButton] = useState(true);
  const { searchTerm, setSearchTerm, proximasAVencer, proximosEventos } =
    useNavContext();
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const dropdownRef = useRef(null); // Referencia al menú desplegable
  const fileInputRef = useRef(null);
  const [selectedProducts, setSelectedProducts] = useState({});
  const [activeTab, setActiveTab] = useState("Todos");

  // Limpia el buscador al cambiar de ruta
  useEffect(() => {
    setSearchTerm("");
  }, [location.pathname, setSearchTerm]);

  const isAnyProductSelected = Object.values(selectedProducts).some(Boolean);

  const handleDeleteSelected = () => {
    const idsAEliminar = Object.keys(selectedProducts).filter(
      (productId) => selectedProducts[productId]
    );

    if (idsAEliminar.length === 0) return;

    Swal.fire({
      title: "¿Estás seguro?",
      text: "¡No podrás deshacer esta acción!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      scrollbarPadding: false,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          for (const productId of idsAEliminar) {
            await deleteDoc(doc(db, "productos", productId));
          }
          const updatedProducts = products.filter(
            (product) => !idsAEliminar.includes(product.id)
          );
          setProducts(updatedProducts);
          Swal.fire({
            title: "¡Eliminado!",
            text: "Los productos seleccionados han sido eliminados.",
            icon: "success",
            scrollbarPadding: false,
          });
        } catch (error) {
          console.error("Error al eliminar los productos:", error);
          Swal.fire(
            "¡Error!",
            `Ha ocurrido un error al intentar eliminar: ${error.message}`,
            "error"
          );
        }
      }
    });
  };

  const handleDeleteProduct = async (productId) => {
    Swal.fire({
      title: "¿Estás seguro?",
      text: "¡No podrás deshacer esta acción!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      scrollbarPadding: false,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteDoc(doc(db, "productos", productId));
          const updatedProducts = products.filter(
            (product) => product.id !== productId
          );
          setProducts(updatedProducts);
          Swal.fire("¡Eliminado!", "El producto ha sido eliminado.", "success");
        } catch (error) {
          console.error("Error al eliminar el producto:", error);
          Swal.fire(
            "¡Error!",
            `Ha ocurrido un error al intentar eliminar el producto: ${error.message}`,
            "error"
          );
        }
      }
    });
  };

  const handleEditSelected = () => {
    // 1) Conseguir qué producto está seleccionado.
    //    Sólo debería haber uno porque limitamos la selección arriba.
    const selectedIds = Object.keys(selectedProducts).filter(
      (productId) => selectedProducts[productId]
    );

    if (selectedIds.length === 0) {
      Swal.fire("Atención", "No has seleccionado ningún producto.", "info");
      return;
    }

    // Si fuera más de uno, podría mostrar un error o alerta:
    if (selectedIds.length > 1) {
      Swal.fire("Atención", "Sólo puedes editar un producto a la vez.", "info");
      return;
    }

    // 2) Tomamos el ID y buscamos el producto completo en `products`.
    const productId = selectedIds[0];
    const productToEdit = products.find((p) => p.id === productId);
    if (!productToEdit) {
      Swal.fire("Error", "No se encontró el producto a editar.", "error");
      return;
    }

    // 3) Guardamos ese producto en `editingProduct` para que el modal muestre sus datos.
    setEditingProduct(productToEdit);

    // 4) Abrimos el modal (así como haces al presionar el botón "Agregar productos")
    setIsModalOpen(true);
  };

  const handleSelectionChange = (newSelected) => {
    setSelectedProducts(newSelected);

    const isSelected = Object.values(newSelected).some(Boolean);
    if (isSelected) {
      // Si se ha seleccionado al menos un producto, abrimos menú
      setShowOptionsMenu(true);
    } else {
      // Si se deseleccionó todo, cerramos menú
      setShowOptionsMenu(false);
    }
  };

  const toggleOptionsMenu = () => {
    // Si va a cerrar y todavía hay productos seleccionados, los deselecciona
    setShowOptionsMenu((prev) => {
      const newValue = !prev;
      if (!newValue && isAnyProductSelected) {
        // Quita selección
        setSelectedProducts({});
      }
      return newValue;
    });
  };

  const handleImportClick = () => {
    fileInputRef.current.click(); // Simula el clic en el input de archivo
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      Swal.fire({
        title: "Error",
        text: "No se seleccionó ningún archivo.",
        icon: "error",
        confirmButtonText: "OK",
        scrollbarPadding: false,
      });
      return;
    }

    const reader = new FileReader();
    const originalConsoleError = console.error;

    // Evitar ciertos warnings de XLSX
    console.error = (message, ...args) => {
      if (message.includes("Bad uncompressed size")) {
        return;
      }
      originalConsoleError(message, ...args);
    };

    reader.onload = async (e) => {
      try {
        // Lee el contenido del archivo Excel
        const data = new Uint8Array(e.target.result);

        // Lee el workbook con XLSX
        const workbook = XLSX.read(data, {
          type: "array",
          cellDates: true,
          raw: true,
        });

        if (!workbook.SheetNames.length) {
          throw new Error("El archivo no contiene hojas válidas.");
        }

        // Toma la primera hoja
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        if (!sheet) {
          throw new Error("No se pudo leer la hoja seleccionada.");
        }

        // Definimos manualmente los encabezados (sin 'range' para incluir todas las filas)
        const headers = [
          "CANTIDAD",
          "UNIDAD",
          "CÓDIGO",
          "DESCRIPCIÓN",
          "PRECIO UNITARIO",
          "CATEGORIA",
          "PROVEEDOR",
        ];

        const jsonData = XLSX.utils.sheet_to_json(sheet, {
          header: headers,
          defval: "",
        });

        console.log("jsonData completo:", jsonData);
        if (!jsonData.length) {
          throw new Error("El archivo no contiene datos válidos.");
        }

        // Referencia a la colección "productos" en Firestore
        const productsCollectionRef = collection(db, "productos");
        // Referencia a la colección "categorias"
        const categoriasCollectionRef = collection(db, "categorias");
        // Referencia a la colección "proveedores"
        const proveedoresCollectionRef = collection(db, "proveedores");

        let nuevosProductos = 0;
        let productosOmitidos = 0;
        // Sets para evitar consultas repetidas
        const categoriasProcesadas = new Set();
        const proveedoresProcesados = new Set();

        // Recorre cada fila del Excel
        for (const row of jsonData) {
          console.log("FILA LEÍDA:", row);

          const cantidad = parseFloat(row["CANTIDAD"] || 0);
          const unidad = row["UNIDAD"] || "";
          const codigo = row["CÓDIGO"] || "";
          const descripcion = row["DESCRIPCIÓN"] || "";
          const precioUnitario = parseCost(row["PRECIO UNITARIO"] || "");
          const categoria = row["CATEGORIA"] || "";
          const proveedor = row["PROVEEDOR"] || "";

          // Si no hay código ni descripción, ignoramos la fila
          if (!codigo && !descripcion) {
            continue;
          }

          // Procesa la categoría (similar a lo anterior)
          if (categoria && !categoriasProcesadas.has(categoria)) {
            const catQuery = query(
              categoriasCollectionRef,
              where("nombre", "==", categoria)
            );
            const catQuerySnapshot = await getDocs(catQuery);
            if (catQuerySnapshot.empty) {
              await addDoc(categoriasCollectionRef, { nombre: categoria });
            }
            categoriasProcesadas.add(categoria);
          }

          // Procesa el proveedor: se guarda solo el nombre de la empresa
          if (proveedor && !proveedoresProcesados.has(proveedor)) {
            const provQuery = query(
              proveedoresCollectionRef,
              where("empresa", "==", proveedor)
            );
            const provQuerySnapshot = await getDocs(provQuery);
            if (provQuerySnapshot.empty) {
              await addDoc(proveedoresCollectionRef, { empresa: proveedor });
            }
            proveedoresProcesados.add(proveedor);
          }

          // Datos del producto a crear
          const productoData = {
            nombre: descripcion,
            numeroDeParteProveedor: codigo,
            costo: precioUnitario,
            categoria: categoria.trim().toUpperCase(),
            proveedor, // Aquí se guarda el nombre del proveedor
            cantidad,
            unidad,
            fechaCreacion: moment().toISOString(),
          };

          // Consulta para ver si ya existe un producto con el mismo código
          const q = query(
            productsCollectionRef,
            where("numeroDeParteProveedor", "==", codigo)
          );
          const querySnapshot = await getDocs(q);

          if (querySnapshot.empty) {
            // Si NO existe, lo crea
            await addDoc(productsCollectionRef, productoData);
            nuevosProductos++;
          } else {
            // Si ya existe, omitimos para evitar duplicados
            productosOmitidos++;
            console.log(`Producto con código '${codigo}' omitido (ya existe).`);
          }
        }

        // Actualiza el estado local para refrescar la tabla
        const updatedDocs = await getDocs(productsCollectionRef);
        const productsData = updatedDocs.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setProducts(productsData);

        Swal.fire({
          title: "Importación exitosa",
          text: `Se agregaron ${nuevosProductos} producto(s) y se omitieron ${productosOmitidos} duplicado(s).`,
          icon: "success",
          confirmButtonText: "OK",
          scrollbarPadding: false,
        });
      } catch (error) {
        console.error("Error al procesar el archivo:", error);
        Swal.fire({
          title: "Error",
          text: `Error al procesar el archivo: ${error.message}`,
          icon: "error",
          confirmButtonText: "OK",
          scrollbarPadding: false,
        });
      } finally {
        console.error = originalConsoleError;
      }
    };

    reader.onerror = () => {
      console.error = originalConsoleError;
      Swal.fire({
        title: "Error",
        text: "Ocurrió un error al leer el archivo. Inténtalo nuevamente.",
        icon: "error",
        confirmButtonText: "OK",
        scrollbarPadding: false,
      });
    };

    reader.readAsArrayBuffer(file);
  };

  const filterProducts = useCallback(
    (searchTerm) => {
      if (!products || products.length === 0) {
        setFilteredProducts([]);
        return;
      }

      const lowerSearch = searchTerm.toLowerCase();

      // Primer filtro: texto de búsqueda
      const searchFiltered = products.filter((product) => {
        const fieldsToSearch = [
          product.nombre,
          product.proveedor,
          product.categoria,
          product.numeroDeParteCliente,
          product.numeroDeParteProveedor,
          product.costo,
        ];
        return fieldsToSearch.some((field) => {
          if (field == null) return false;
          return String(field).toLowerCase().includes(lowerSearch);
        });
      });

      // Segundo filtro: tipo (según tab)
      let finalFiltered = searchFiltered;
      if (activeTab === "Productos") {
        finalFiltered = searchFiltered.filter((p) => p.tipo === "Producto");
      } else if (activeTab === "Servicios") {
        finalFiltered = searchFiltered.filter((p) => p.tipo === "Servicio");
      }
      // Si el tab es "Todos", no filtramos por `tipo`

      setFilteredProducts(finalFiltered);
    },
    [products, activeTab] // ojo con las dependencias
  );

  useEffect(() => {
    filterProducts(searchTerm);
  }, [searchTerm, filterProducts, activeTab]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const productsSnapshot = await getDocs(collection(db, "productos"));
        const productsData = productsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setProducts(productsData);
        setIsLoading(false);
      } catch (error) {
        console.error("Error al obtener los productos:", error);
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, []);

  useEffect(() => {
    filterProducts(searchTerm || "");
  }, [searchTerm, filterProducts]);

  const handleAddProduct = async (newProduct) => {
    try {
      const { id, ...productData } = newProduct; // Omitir 'id'
      const docRef = await addDoc(collection(db, "productos"), productData);
      setProducts((prevProducts) => [
        ...prevProducts,
        { id: docRef.id, ...productData },
      ]);
      setIsModalOpen(false);
      setEditingProduct(null);
    } catch (error) {
      console.error("Error al agregar el producto:", error);
      Swal.fire(
        "¡Error!",
        `Ha ocurrido un error al agregar el producto: ${error.message}`,
        "error"
      );
    }
  };

  const handleEditProduct = async (updatedProduct) => {
    try {
      const { id, ...data } = updatedProduct; // Desestructura el id
      await updateDoc(doc(db, "productos", id), data); // Usa el id para actualizar
      const updatedProducts = products.map((product) =>
        product.id === id ? updatedProduct : product
      );
      setProducts(updatedProducts);
      setIsModalOpen(false);
      setEditingProduct(null);
    } catch (error) {
      console.error("Error al editar el producto:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Hubo un problema al actualizar el producto.",
        confirmButtonText: "Aceptar",
        scrollbarPadding: false,
      });
    }
  };

  const handleAdjustPrices = async (increase, percentage) => {
    const adjustFactor = parseFloat(
      (1 + (increase ? percentage : -percentage) / 100).toFixed(2)
    );
    const updatePromises = products.map((product) => {
      const newCost = parseFloat((product.costo * adjustFactor).toFixed(2));
      const productRef = doc(db, "productos", product.id);
      return updateDoc(productRef, { costo: newCost });
    });

    try {
      await Promise.all(updatePromises);
      Swal.fire({
        title: "¡Éxito!",
        text: "Precios actualizados correctamente en Firebase.",
        icon: "success",
        confirmButtonText: "OK",
      });
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: "Ocurrió un error al actualizar los precios.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  return (
    <>
      <Nav
        handleSearch={searchTerm}
        proximasAVencer={proximasAVencer}
        proximosEventos={proximosEventos}
      />

      <div className="task-filters">
        <div className="tabs-container">
          <button
            className={`tab-button ${activeTab === "Todos" ? "active" : ""}`}
            onClick={() => setActiveTab("Todos")}
          >
            Todos
          </button>

          <button
            className={`tab-button ${
              activeTab === "Productos" ? "active" : ""
            }`}
            onClick={() => setActiveTab("Productos")}
          >
            Productos
          </button>

          <button
            className={`tab-button ${
              activeTab === "Servicios" ? "active" : ""
            }`}
            onClick={() => setActiveTab("Servicios")}
          >
            Servicios
          </button>
        </div>

        <div className="task-filters-right">
          <div
            className="header-actions-proyectos"
            style={{ paddingLeft: "10px", marginRight: "10px" }}
          >
            <div className="buscador-pedidos">
              <i className="bi bi-search"></i>
              <input
                type="text"
                className="buscador-input"
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  filterProducts(e.target.value);
                }}
              />
            </div>
          </div>

          {/* Contenedor con position relative */}
          <div style={{ position: "relative" }}>
            <button className="view-button" onClick={toggleOptionsMenu}>
              <i className="bi bi-three-dots-vertical"></i>
            </button>

            {showOptionsMenu &&
              (isAnyProductSelected ? (
                <div
                  className="dropdown-options"
                  ref={dropdownRef}
                  style={{
                    position: "absolute",
                    top: "calc(100% + 5px)", // un pequeño margen debajo del botón
                    right: 0,
                    zIndex: 9999,

                    backgroundColor: "#fff",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                    minWidth: "120px", // ancho mínimo
                  }}
                >
                  <p
                    onClick={handleDeleteSelected}
                    style={{
                      margin: 0,
                      padding: "8px 12px",
                      cursor: "pointer",
                    }}
                  >
                    <i
                      className="bi bi-trash"
                      style={{ marginRight: "10px" }}
                    ></i>
                    Eliminar
                  </p>
                  <p
                    onClick={handleEditSelected}
                    style={{
                      margin: 0,
                      padding: "8px 12px",
                      cursor: "pointer",
                    }}
                  >
                    <i
                      className="bi bi-pencil-square"
                      style={{ marginRight: "10px" }}
                    ></i>
                    Editar
                  </p>
                </div>
              ) : (
                <div
                  className="dropdown-options"
                  ref={dropdownRef}
                  style={{
                    position: "absolute",
                    top: "calc(100% + 5px)",
                    right: 0,
                    zIndex: 9999,

                    backgroundColor: "#fff",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                    minWidth: "120px",
                  }}
                >
                  <p
                    onClick={handleImportClick}
                    style={{
                      margin: 0,
                      padding: "8px 12px",
                      cursor: "pointer",
                    }}
                  >
                    <i
                      className="bi bi-box-arrow-in-down"
                      style={{ marginRight: "10px" }}
                    ></i>
                    Importar
                  </p>
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    accept=".xlsx, .xls"
                    onChange={handleFileUpload}
                  />
                  <p
                    style={{
                      margin: 0,
                      padding: "8px 12px",
                      cursor: "pointer",
                    }}
                  >
                    <i
                      className="bi bi-box-arrow-up"
                      style={{ marginRight: "10px" }}
                    ></i>
                    Exportar
                  </p>
                </div>
              ))}
          </div>

          {showNuevoButton && (
            <button
              className="new-task-button"
              onClick={() => {
                setIsModalOpen(true);
                setEditingProduct(null);
              }}
            >
              + Agregar
            </button>
          )}

          <ProductButton
            showNuevoButton={showNuevoButton}
            onClick={handleAdjustPrices}
            setShowNuevoButton={setShowNuevoButton}
          />
        </div>
      </div>

      <Sidebar />
      {isLoading ? (
        <Spinner />
      ) : filteredProducts.length === 0 ? (
        // Si no hay productos filtrados:
        searchTerm ? (
          // Hay búsqueda activa pero 0 resultados
          <div className="no-resultados">
            <div
              className="mensaje-sin-resultados"
              style={{ marginTop: "30px" }}
            >
              <p style={{ fontSize: "14px" }}>No hay resultados disponibles.</p>
            </div>
            <div className="imagen-sin-resultados">
              <img
                src="/img/noSearch.svg"
                alt="Sin resultados"
                className="sin-resultados-imagen"
              />
            </div>
          </div>
        ) : activeTab === "Productos" ? (
          <p style={{ marginTop: "30px", textAlign: "center" }}>
            No hay productos todavía.
          </p>
        ) : activeTab === "Servicios" ? (
          <p style={{ marginTop: "30px", textAlign: "center" }}>
            No hay servicios todavía.
          </p>
        ) : (
          // activeTab === "Todos"
          <p style={{ marginTop: "30px", textAlign: "center" }}>
            No hay artículos todavía.
          </p>
        )
      ) : (
        // Caso en que sí hay resultados
        <ProductTable
          products={filteredProducts}
          selectedProducts={selectedProducts}
          setSelectedProducts={setSelectedProducts}
          onEditProduct={(productId) => {
            const productToEdit = products.find(
              (product) => product.id === productId
            );
            setEditingProduct(productToEdit);
            setIsModalOpen(true);
          }}
          onDeleteProduct={handleDeleteProduct}
          onSelectionChange={handleSelectionChange}
        />
      )}

      <AddProductModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={editingProduct ? handleEditProduct : handleAddProduct}
        editingProduct={editingProduct}
      />
    </>
  );
};

export default ArticulosCompra;
